import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'tla_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'tla_cookie_preferences';

function initPostHog() {
  const cfg = window.__PH_CONFIG;
  if (cfg && window.posthog && typeof window.posthog.init === 'function' && !window.posthog.__loaded) {
    window.posthog.init(cfg.key, cfg);
    window.posthog.__loaded = true;
  }
}

const DEFAULT_PREFERENCES = {
  essential: true,      // Always required
  functional: false,    // Language, theme preferences
  analytics: false,     // Usage analytics
  marketing: false      // Marketing/advertising (not used currently)
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPrefs = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || '{}');
        const merged = { ...DEFAULT_PREFERENCES, ...savedPrefs };
        setPreferences(merged);
        if (merged.analytics) initPostHog();
      } catch {
        setPreferences(DEFAULT_PREFERENCES);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const handleAcceptEssential = () => {
    saveConsent(DEFAULT_PREFERENCES);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, new Date().toISOString());
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    if (prefs.analytics) initPostHog();
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-slate-100/95 backdrop-blur-lg border-t border-slate-800 shadow-2xl"
        role="dialog"
        aria-labelledby="cookie-title"
        aria-describedby="cookie-description"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            {/* Icon & Text */}
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-military-gold/20 rounded-lg flex-shrink-0">
                  <Cookie className="w-5 h-5 text-military-gold" />
                </div>
                <div>
                  <h2 id="cookie-title" className="text-lg font-bold text-slate-800 mb-1">
                    Cookie Settings
                  </h2>
                  <p id="cookie-description" className="text-sm text-slate-600 leading-relaxed">
                    We use optional cookies for analytics. Click{' '}
                    <strong className="text-slate-700">Reject Optional</strong> to use essential cookies only,
                    or <strong className="text-slate-700">Accept All</strong> to enable analytics.
                    {' '}Use <strong className="text-slate-700">Customize</strong> to choose per category. Learn more in our{' '}
                    <Link to="/privacy" className="text-signal-blue hover:underline">
                      Privacy Policy
                    </Link>.
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons — order: Reject Optional | Customize | Accept All */}
            <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
              <Button
                onClick={handleAcceptEssential}
                className="bg-slate-700 hover:bg-slate-600 text-white font-medium"
                data-testid="cookie-reject-btn"
              >
                Reject Optional
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSettings(true)}
                className="border-slate-600 text-slate-400 hover:bg-slate-800 text-sm"
                data-testid="cookie-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Customize
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-gradient-to-r from-military-gold to-amber-600 hover:from-military-gold/90 hover:to-amber-500 text-slate-950 font-semibold"
                data-testid="cookie-accept-all-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div 
            className="glass-card w-full max-w-lg max-h-[90vh] overflow-auto"
            role="dialog"
            aria-labelledby="settings-title"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-signal-blue/20 rounded-lg">
                    <Shield className="w-5 h-5 text-signal-blue" />
                  </div>
                  <h3 id="settings-title" className="text-lg font-bold text-slate-800">
                    Cookie Preferences
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-slate-600 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                  aria-label="Close settings"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-slate-600 mb-6">
                Manage your cookie preferences below. Essential cookies are required for basic site functionality 
                and cannot be disabled.
              </p>

              {/* Cookie Categories */}
              <div className="space-y-4">
                {/* Essential */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-200">Essential Cookies</h4>
                    <div className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded font-medium">
                      Always Active
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">
                    Required for the website to function. Includes session management, authentication, 
                    and security features. Cannot be disabled.
                  </p>
                </div>

                {/* Functional */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-200">Functional Cookies</h4>
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.functional ? 'bg-signal-blue' : 'bg-slate-600'
                      }`}
                      role="switch"
                      aria-checked={preferences.functional}
                      data-testid="cookie-functional-toggle"
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.functional ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-slate-600">
                    Remember your preferences like language settings, theme choices, and customizations 
                    to provide a personalized experience.
                  </p>
                </div>

                {/* Analytics */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-200">Analytics Cookies</h4>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-signal-blue' : 'bg-slate-600'
                      }`}
                      role="switch"
                      aria-checked={preferences.analytics}
                      data-testid="cookie-analytics-toggle"
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.analytics ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-slate-600">
                    Help us understand how visitors interact with our website. This data is anonymized 
                    and used to improve our services.
                  </p>
                </div>

                {/* Marketing */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-200">Marketing Cookies</h4>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-signal-blue' : 'bg-slate-600'
                      }`}
                      role="switch"
                      aria-checked={preferences.marketing}
                      data-testid="cookie-marketing-toggle"
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          preferences.marketing ? 'left-7' : 'left-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-slate-600">
                    Used to deliver relevant advertisements and track the effectiveness of our 
                    marketing campaigns. Currently not in use.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowSettings(false)}
                  className="flex-1 border-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-signal-blue to-blue-600"
                  data-testid="cookie-save-prefs-btn"
                >
                  Save Preferences
                </Button>
              </div>

              {/* Link to Privacy Policy */}
              <p className="text-xs text-slate-800 text-center mt-4">
                For more information, please read our{' '}
                <Link to="/privacy" className="text-signal-blue hover:underline" onClick={() => setShowSettings(false)}>
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Hook to check cookie preferences
export function useCookiePreferences() {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    try {
      const savedPrefs = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || '{}');
      setPreferences({ ...DEFAULT_PREFERENCES, ...savedPrefs });
    } catch {
      setPreferences(DEFAULT_PREFERENCES);
    }
  }, []);

  return preferences;
}

// Function to reset cookie consent (for testing or settings page)
export function resetCookieConsent() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_PREFERENCES_KEY);
  window.location.reload();
}
